import React, {
  useState,
  useMemo,
  useEffect,
  useRef,
  useImperativeHandle,
  forwardRef,
} from 'react';
import { useTranslation } from 'react-i18next';
import { useQuoteStore } from '../../stores/quoteStore';
import { Quote, QuoteJob, QuoteTaskInput, QuotePricingType } from '../../types/quote';
import { Job } from '../../types/job';
import FormattedCurrency from '../common/FormattedCurrency';
import { Trash2, Plus } from 'lucide-react';
import { InputGroup } from '../common/InputGroup';
import { DropdownSelect } from '../common/DropdownSelect';

const PRICING_TYPES: QuotePricingType[] = [
  'hourly',
  'flat_rate',
  'unit',
  'fee',
  'adjustment',
  'tax',
];

const TYPES_WITH_QUANTITY: QuotePricingType[] = ['hourly', 'unit'];

const defaultLine: QuoteTaskInput = {
  name: '',
  pricing_type: 'hourly',
  quantity: 1,
  unit_price: 0,
};

function linesAndNotesEqual(
  a: { lines: QuoteTaskInput[]; notes: string },
  b: { lines: QuoteTaskInput[]; notes: string }
): boolean {
  if (a.notes.trim() !== b.notes.trim()) return false;
  if (a.lines.length !== b.lines.length) return false;
  return a.lines.every((line, i) => {
    const other = b.lines[i];
    return (
      (line.name ?? '').trim() === (other.name ?? '').trim() &&
      line.pricing_type === other.pricing_type &&
      Number(line.quantity ?? 0) === Number(other.quantity ?? 0) &&
      Number(line.unit_price ?? 0) === Number(other.unit_price ?? 0)
    );
  });
}

export interface OfferLinesEditorHandle {
  save: () => Promise<boolean>;
}

interface OfferLinesEditorProps {
  initialLines: QuoteTaskInput[];
  initialNotes: string | null;
  job: QuoteJob | Job;
  quote?: Quote;
  parentQuoteId?: string | null;
  canModify: boolean;
  onDirtyChange?: (dirty: boolean) => void;
}

const OfferLinesEditor = forwardRef<OfferLinesEditorHandle, OfferLinesEditorProps>(
  function OfferLinesEditor(
    { initialLines, initialNotes, job, quote, parentQuoteId, canModify, onDirtyChange },
    ref
  ) {
    const { t } = useTranslation('quotes');
    const { addQuoteWithTasks } = useQuoteStore();

    const [lines, setLines] = useState<QuoteTaskInput[]>(
      initialLines.length > 0 ? initialLines : [{ ...defaultLine }]
    );

    const [notes, setNotes] = useState(initialNotes ?? '');
    const [saving, setSaving] = useState(false);
    const [saveError, setSaveError] = useState<string | null>(null);
    const [lineErrors, setLineErrors] = useState<{
      [idx: number]: { name?: string; quantity?: string; unit_price?: string };
    }>({});
    const [deletedIds, setDeletedIds] = useState<string[]>([]);

    const savedSnapshotRef = useRef<{ lines: QuoteTaskInput[]; notes: string }>({
      lines: initialLines.length > 0 ? initialLines : [{ ...defaultLine }],
      notes: initialNotes ?? '',
    });

    const currentSnapshot = useMemo(() => ({ lines, notes }), [lines, notes]);

    const isDirty = !linesAndNotesEqual(currentSnapshot, savedSnapshotRef.current);

    useEffect(() => {
      onDirtyChange?.(isDirty);
    }, [isDirty, onDirtyChange]);

    const handleLineChange = (idx: number, field: keyof QuoteTaskInput, value: string | number) => {
      setLines(prev =>
        prev.map((line, i) => {
          if (i !== idx) return line;
          const next = { ...line, [field]: value };
          if (
            field === 'pricing_type' &&
            !TYPES_WITH_QUANTITY.includes(value as QuotePricingType)
          ) {
            next.quantity = 1;
          }
          return next;
        })
      );

      setLineErrors(prev => ({
        ...prev,
        [idx]: { ...(prev[idx] || {}), [field]: undefined },
      }));
    };

    const addLine = () => {
      setLines(prev => [...prev, { ...defaultLine }]);
    };

    const removeLine = (idx: number) => {
      const line = lines[idx];
      if (line.id) setDeletedIds(prev => [...prev, line.id!]);
      setLines(prev => prev.filter((_, i) => i !== idx));
    };

    const lineTotal = (line: QuoteTaskInput) => {
      if (TYPES_WITH_QUANTITY.includes(line.pricing_type)) {
        return Number(line.quantity || 0) * Number(line.unit_price || 0);
      }
      return Number(line.unit_price || 0);
    };

    const validate = (): boolean => {
      const errors: typeof lineErrors = {};

      lines.forEach((line, idx) => {
        const err: { name?: string; quantity?: string; unit_price?: string } = {};

        if (!(line.name ?? '').trim())
          err.name = t('common:error.fieldRequired', 'Ce champ est requis');

        if (TYPES_WITH_QUANTITY.includes(line.pricing_type)) {
          if (line.quantity === undefined || Number(line.quantity) <= 0)
            err.quantity = t('common:error.fieldRequired', 'Ce champ est requis');
        }

        if (line.unit_price === undefined || Number(line.unit_price) < 0)
          err.unit_price = t('common:error.fieldRequired', 'Ce champ est requis');

        if (Object.keys(err).length) errors[idx] = err;
      });

      setLineErrors(errors);
      return Object.keys(errors).length === 0;
    };

    const handleSave = async (): Promise<boolean> => {
      if (!canModify) return false;
      if (!validate()) return false;

      setSaving(true);
      setSaveError(null);

      try {
        const updatedQuote = await addQuoteWithTasks({
          jobId: job.id,
          quoteId: quote?.id,
          parentQuoteId: parentQuoteId ?? quote?.parent_quote_id ?? null,
          tasks: lines.map(line => ({
            ...line,
            quantity: TYPES_WITH_QUANTITY.includes(line.pricing_type) ? line.quantity : 1,
          })),
          message: notes.trim() || undefined,
          deletedTaskIds: deletedIds.length ? deletedIds : undefined,
        });

        setDeletedIds([]);

        if (updatedQuote?.tasks?.length) {
          const persistedLines: QuoteTaskInput[] = updatedQuote.tasks.map(task => ({
            id: task.id,
            name: task.name,
            pricing_type: task.pricing_type,
            quantity: task.quantity,
            unit_price: task.unit_price,
          }));
          setLines(persistedLines);
          savedSnapshotRef.current = {
            lines: persistedLines.map(l => ({ ...l })),
            notes,
          };
        } else {
          savedSnapshotRef.current = {
            lines: lines.map(l => ({ ...l })),
            notes,
          };
        }

        onDirtyChange?.(false);
        return true;
      } catch (err) {
        const msg = (err as { message?: string })?.message ?? '';

        if (msg.includes('QUOTE_ALREADY_SUBMITTED')) {
          setSaveError(t('createQuoteModal.quoteAlreadySubmitted', "L'estimé est déjà soumise"));
        } else {
          setSaveError(
            t('createQuoteModal.unknownErrorCreatingTasks', 'Une erreur inconnue est survenue')
          );
        }
        return false;
      } finally {
        setSaving(false);
      }
    };

    useImperativeHandle(ref, () => ({
      save: handleSave,
    }));

    return (
      <div className="space-y-6">
        <div className="space-y-4">
          {/* Desktop: single grid so header and all rows share the same column tracks */}
          <div className="hidden md:grid gap-x-4 gap-y-2 px-1 grid-cols-[3fr_1fr_1fr_1fr_1fr_auto] items-center">
            {/* Header row */}
            <span className="text-xs font-medium uppercase tracking-wide text-secondary border-b border-[#E1E9F0] pb-2">
              {t('createQuoteModal.taskDescriptionColumnHeader', 'Description de la tâche')}
            </span>
            <span className="text-xs font-medium uppercase tracking-wide text-secondary border-b border-[#E1E9F0] pb-2">
              {t('createQuoteModal.typeColumnHeader', 'Type')}
            </span>
            <span className="text-xs font-medium uppercase tracking-wide text-secondary border-b border-[#E1E9F0] pb-2">
              {t('createQuoteModal.quantityColumnHeader', 'Quantité')}
            </span>
            <span className="text-xs font-medium uppercase tracking-wide text-secondary border-b border-[#E1E9F0] pb-2">
              {t('createQuoteModal.unitPriceColumnHeader', 'Prix unitaire')}
            </span>
            <span className="text-xs font-medium uppercase tracking-wide text-secondary border-b border-[#E1E9F0] pb-2">
              {t('createQuoteModal.totalLabel', 'Total')}
            </span>
            <span
              className="text-xs font-medium uppercase tracking-wide text-secondary border-b border-[#E1E9F0] pb-6"
              aria-hidden
            />
            {/* Data rows: each line contributes 6 cells to the same grid */}
            {lines.map((line, idx) => {
              const quantityEnabled = TYPES_WITH_QUANTITY.includes(line.pricing_type);
              const rowBorder = 'border-b border-[#E1E9F0]/50 pb-2';
              return (
                <React.Fragment key={idx}>
                  <div className={rowBorder}>
                    <InputGroup
                      label=""
                      value={line.name}
                      onChange={e => handleLineChange(idx, 'name', e.target.value)}
                      error={!!lineErrors[idx]?.name}
                      errorMessage={lineErrors[idx]?.name}
                      disabled={!canModify || saving}
                      placeholder={t(
                        'createQuoteModal.taskDescriptionPlaceholder',
                        'Voici le nom de la tâche'
                      )}
                    />
                  </div>
                  <div className={rowBorder}>
                    <DropdownSelect<QuotePricingType>
                      value={line.pricing_type}
                      options={PRICING_TYPES}
                      getOptionLabel={type => t(`createQuoteModal.pricingType.${type}`)}
                      getOptionValue={type => type}
                      onChange={(_, value) => handleLineChange(idx, 'pricing_type', value!)}
                      disabled={!canModify || saving}
                    />
                  </div>
                  <div className={rowBorder}>
                    <InputGroup
                      label=""
                      type="number"
                      value={quantityEnabled ? line.quantity : 1}
                      onChange={e => handleLineChange(idx, 'quantity', Number(e.target.value))}
                      error={!!lineErrors[idx]?.quantity}
                      errorMessage={lineErrors[idx]?.quantity}
                      disabled={!canModify || saving || !quantityEnabled}
                    />
                  </div>
                  <div className={rowBorder}>
                    <InputGroup
                      label=""
                      type="number"
                      value={line.unit_price}
                      onChange={e => handleLineChange(idx, 'unit_price', Number(e.target.value))}
                      error={!!lineErrors[idx]?.unit_price}
                      errorMessage={lineErrors[idx]?.unit_price}
                      disabled={!canModify || saving}
                    />
                  </div>
                  <div
                    className={`flex min-h-[2.5rem] items-center font-semibold text-primary ${rowBorder}`}
                  >
                    <FormattedCurrency value={lineTotal(line)} />
                  </div>
                  <div className={`flex items-center justify-end ${rowBorder}`}>
                    {canModify ? (
                      <button
                        type="button"
                        onClick={() => removeLine(idx)}
                        disabled={lines.length <= 1}
                        className="text-secondary hover:text-red-600 disabled:opacity-50 p-1"
                        aria-label={t('common:form_actions.delete')}
                      >
                        <Trash2 className="h-5 w-5" />
                      </button>
                    ) : (
                      <span className="w-10" aria-hidden />
                    )}
                  </div>
                </React.Fragment>
              );
            })}
          </div>

          {/* Mobile: stacked cards per line */}
          <div className="md:hidden space-y-4">
            {lines.map((line, idx) => {
              const quantityEnabled = TYPES_WITH_QUANTITY.includes(line.pricing_type);
              const mobileLabelClass =
                'block font-ambit text-[0.9375rem] not-italic font-normal leading-[1.25rem] text-secondary mb-1';

              return (
                <div key={idx} className="grid gap-4 py-2 border-b border-[#E1E9F0]/50 grid-cols-1">
                  <div>
                    <span className={mobileLabelClass}>
                      {t('createQuoteModal.taskDescriptionColumnHeader', 'Description de la tâche')}
                    </span>
                    <InputGroup
                      label=""
                      value={line.name}
                      onChange={e => handleLineChange(idx, 'name', e.target.value)}
                      error={!!lineErrors[idx]?.name}
                      errorMessage={lineErrors[idx]?.name}
                      disabled={!canModify || saving}
                      placeholder={t(
                        'createQuoteModal.taskDescriptionPlaceholder',
                        'Voici le nom de la tâche'
                      )}
                    />
                  </div>
                  <div>
                    <span className={mobileLabelClass}>
                      {t('createQuoteModal.typeColumnHeader', 'Type')}
                    </span>
                    <DropdownSelect<QuotePricingType>
                      value={line.pricing_type}
                      options={PRICING_TYPES}
                      getOptionLabel={type => t(`createQuoteModal.pricingType.${type}`)}
                      getOptionValue={type => type}
                      onChange={(_, value) => handleLineChange(idx, 'pricing_type', value!)}
                      disabled={!canModify || saving}
                    />
                  </div>
                  <div>
                    <span className={mobileLabelClass}>
                      {t('createQuoteModal.quantityColumnHeader', 'Quantité')}
                    </span>
                    <InputGroup
                      label=""
                      type="number"
                      value={quantityEnabled ? line.quantity : 1}
                      onChange={e => handleLineChange(idx, 'quantity', Number(e.target.value))}
                      error={!!lineErrors[idx]?.quantity}
                      errorMessage={lineErrors[idx]?.quantity}
                      disabled={!canModify || saving || !quantityEnabled}
                    />
                  </div>
                  <div>
                    <span className={mobileLabelClass}>
                      {t('createQuoteModal.unitPriceColumnHeader', 'Prix unitaire')}
                    </span>
                    <InputGroup
                      label=""
                      type="number"
                      value={line.unit_price}
                      onChange={e => handleLineChange(idx, 'unit_price', Number(e.target.value))}
                      error={!!lineErrors[idx]?.unit_price}
                      errorMessage={lineErrors[idx]?.unit_price}
                      disabled={!canModify || saving}
                    />
                  </div>
                  <div>
                    <span className={mobileLabelClass}>
                      {t('createQuoteModal.totalLabel', 'Total')}
                    </span>
                    <div className="flex min-h-[2.5rem] items-center font-semibold text-primary">
                      <FormattedCurrency value={lineTotal(line)} />
                    </div>
                  </div>
                  {canModify && (
                    <div className="flex items-center justify-end">
                      <button
                        type="button"
                        onClick={() => removeLine(idx)}
                        disabled={lines.length <= 1}
                        className="text-secondary hover:text-red-600 disabled:opacity-50 p-1"
                        aria-label={t('common:form_actions.delete')}
                      >
                        <Trash2 className="h-5 w-5" />
                      </button>
                    </div>
                  )}
                </div>
              );
            })}
          </div>
        </div>

        {canModify && (
          <div className="flex justify-start pt-1">
            <button
              type="button"
              onClick={addLine}
              disabled={saving}
              className="flex items-center gap-2 text-primary font-ambit text-[1.0625rem] font-bold leading-[1.5625rem] hover:opacity-80 disabled:opacity-50 focus:outline-none focus:ring-2 focus:ring-primary/30 rounded"
            >
              <Plus className="h-4 w-4 shrink-0" aria-hidden />
              <span>{t('createQuoteModal.addLineButtonLabel', 'Ajouter une ligne')}</span>
            </button>
          </div>
        )}

        <div className="space-y-2">
          <label className="block text-xs font-medium uppercase tracking-wide text-secondary">
            {t('createQuoteModal.additionalNotesLabel', 'Notes additionnelles')}
          </label>
          <InputGroup
            label=""
            as="textarea"
            value={notes}
            onChange={e => setNotes(e.target.value)}
            disabled={!canModify || saving}
            placeholder={t(
              'createQuoteModal.additionalNotesPlaceholder',
              'Précisez votre méthodologie, vos délais ou toute autre information pertinente pour le client...'
            )}
          />
        </div>

        {saveError && <p className="text-sm text-[#F04438]">{saveError}</p>}
      </div>
    );
  }
);

export default OfferLinesEditor;
